import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import { Alert } from '@app/components/core/alert/Alert'
import {
  ImageEditRow,
  type ImagePreview,
  type ExistingImage,
} from '@app/components/blocks/imageEditRow/ImageEditRow'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useState } from 'react'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { useEditContainerClose } from '../shared/useEditContainerClose'
import { FieldEditActions } from '../shared/FieldEditActions'

export type MainDetailsImagesEditProps = {
  initiativeId: number
  existingImages: ExistingImage[]
}

type UploadableImagePreview = ImagePreview & {
  file: File
  preview: string
}

const isUploadablePreview = (
  preview: ImagePreview,
): preview is UploadableImagePreview =>
  preview.file instanceof File && typeof preview.preview === 'string'

export const MainDetailsImagesEdit = ({
  initiativeId,
  existingImages,
}: MainDetailsImagesEditProps) => {
  const close = useEditContainerClose()
  if (!close) {
    throw new Error('MainDetailsImagesEdit requires an EditContainer ancestor')
  }

  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()
  const translations = useTranslations().app
  const common = translations.common
  const creation = translations.dashboard.initiative.creation

  const [addedImages, setAddedImages] = useState<ImagePreview[]>([])
  const [removedImages, setRemovedImages] = useState<number[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const cleanupObjectUrls = useCallback((previews: ImagePreview[]) => {
    previews.forEach((preview) => {
      if (typeof preview.preview === 'string') {
        URL.revokeObjectURL(preview.preview)
      }
    })
  }, [])

  const save = useCallback(() => {
    setIsSaving(true)

    const run = async () => {
      if (removedImages.length > 0) {
        await Promise.all(
          removedImages.map((fileId) => client.call('delete-file', { fileId })),
        )
      }

      const uploadableImages = addedImages.filter(isUploadablePreview)
      if (uploadableImages.length > 0) {
        await Promise.all(
          uploadableImages.map((preview) =>
            client.stream('upload-file', {
              mimeType: preview.file.type,
              contents: preview.file,
              size: preview.file.size,
              fileName: `initiative-media-${Date.now()}-${preview.file.name}`,
              payload: { initiativeImage: { id: initiativeId } },
            }),
          ),
        )
      }
    }

    void run()
      .then(() => {
        showSuccessToast(common.toastSuccess)
        cleanupObjectUrls(addedImages)
        setAddedImages([])
        setRemovedImages([])
        close()
      })
      .catch(() => showFailToast())
      .finally(() => setIsSaving(false))
  }, [
    addedImages,
    cleanupObjectUrls,
    client,
    close,
    common.toastSuccess,
    initiativeId,
    removedImages,
    showFailToast,
    showSuccessToast,
  ])

  const cancel = useCallback(() => {
    cleanupObjectUrls(addedImages)
    setAddedImages([])
    setRemovedImages([])
    close()
  }, [addedImages, cleanupObjectUrls, close])

  return (
    <Column gap="1rem" alignItems="stretch">
      <Column alignItems="stretch" gap={0}>
        <Text.Medium>{creation.imagesSubtitle}</Text.Medium>
      </Column>

      <ImageEditRow
        existingImages={existingImages}
        newImages={addedImages}
        removedImages={removedImages}
        setNewImages={setAddedImages}
        setRemovedImages={setRemovedImages}
        extraPlaceholdersDisplayed={3}
        iconStyle={{
          color: 'var(--palette-blue-400)',
        }}
      />

      <Alert variant="info" size="medium" message={creation.imagesInfoAlert} />

      <FieldEditActions
        onCancel={cancel}
        onSave={save}
        saveDisabled={isSaving}
      />
    </Column>
  )
}
